import { computed, ref, watch, onMounted } from "vue";
import { useFirebase } from "../helpers/firebase";
import { useAlertStore } from "../store/alertStore";
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";

// Module-level shared state — same pattern as useFirebase's module-level refs.
// All callers of usePushNotifications() share these refs so that Account.vue
// buttons can control the same popups that BethsLayout renders.
const isEnableNotificationsPopupVisible = ref(false);
const isDisableNotificationsPopupVisible = ref(false);
const isIOSNotInstalledPopupVisible = ref(false);
const attemptingNotificationsOperation = ref(false);
const notificationsError = ref<string | null>(null);
const notificationsBlocked = ref(false);
const isConfirmingOptOutAll = ref(false);
const askMeNextTime = ref(false);

function openEnablePopup() {
  attemptingNotificationsOperation.value = false;
  notificationsError.value = null;
  isEnableNotificationsPopupVisible.value = true;
}

function openDisablePopup() {
  attemptingNotificationsOperation.value = false;
  notificationsError.value = null;
  isDisableNotificationsPopupVisible.value = true;
}

/**
 * Lightweight accessor for components that only need to read notification state
 * and trigger popups (e.g. Account.vue), without registering lifecycle hooks.
 */
export function usePushNotificationActions() {
  const { hasPermission } = useFirebase();
  const userStore = useUserStore();

  const notificationsEnabled = computed(() => {
    return hasPermission.value;
  });
  const isTestUser = computed(() => {
    return userStore.user?.loginEmail.endsWith(".test") ?? false;
  });

  return {
    notificationsEnabled,
    isTestUser,
    openEnablePopup,
    openDisablePopup,
  };
}

/**
 * Full push notification composable — call from the layout that owns the popups
 * (BethsLayout). Registers onMounted/watch hooks for auto-startup.
 */
export function usePushNotifications() {
  const alertStore = useAlertStore();
  const bldgStore = useBldgStore();
  const userStore = useUserStore();

  const isLoggedIn = computed(() => userStore.isLoggedIn);
  const property = computed(() => bldgStore.property);

  var {
    hasPermission,
    currentToken,
    incomingMessage,
    alreadyRefreshedThisSession,
    tryGetNotificationPermission,
    unsubscribeNotifications,
    setTokenSentToServer,
    isManuallyUnsubscribed,
    setManuallyUnsubscribed,
    browserName,
    isIOS,
    isAndroid,
    isMacOS,
    isWindows,
    isStandalone,
    isSafari,
    isEdge,
    isChrome,
    isFirefox,
    isiOS17plus,
    isMessagingSupported,
  } = useFirebase();

  const notificationsEnabled = computed(() => {
    return hasPermission.value;
  });

  var isTestUser = computed(() => {
    return userStore.user?.loginEmail.endsWith(".test") ?? false;
  });

  // Whether the notification banner should show (not yet granted, not opted out, eligible)
  const showNotificationBanner = computed(() => {
    // Never offer to enable push where the browser cannot deliver it.
    if (!isMessagingSupported) return false;
    if (
      !isLoggedIn.value ||
      !property.value?.isAlertEnabled ||
      !property.value?.isPushNotificationsEnabled ||
      !isTestUser.value
    )
      return false;
    if (askMeNextTime.value) return false;
    if (userStore.user?.isPushOptedOut) return false;
    if (isManuallyUnsubscribed()) return false;
    if (hasPermission.value) return false;
    // Don't show banner if the enable popup is already open
    if (isEnableNotificationsPopupVisible.value) return false;
    return true;
  });

  function dismissNotification() {
    incomingMessage.value = null;
  }

  function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function tryGetNotificationPermissionAtStartup() {
    // Browsers without the Push API (iOS in-app WebViews) have no messaging instance,
    // and may not define Notification at all - referencing it below would throw.
    if (!isMessagingSupported || typeof Notification === "undefined") return;

    if (
      !isLoggedIn.value ||
      !property.value?.isAlertEnabled ||
      !property.value?.isPushNotificationsEnabled ||
      !isTestUser.value
    )
      return;

    // console.log("", "tryGetNotificationPermissionAtStartup", {
    //   isLoggedIn: isLoggedIn.value,
    //   property: property.value,
    // });

    const user = userStore.user;

    // If user has opted out globally, do nothing
    if (user?.isPushOptedOut) return;

    // If user has opted out on this device, do nothing
    if (isManuallyUnsubscribed()) return;

    // If permission already granted, do the session-based token refresh
    if (Notification.permission === "granted") {
      if (!alreadyRefreshedThisSession.value) {
        const err = await tryGetNotificationPermission(false);
        if (hasPermission.value && currentToken.value) {
          const userName = user?.loginEmail;
          if (
            userName &&
            (await alertStore.updateNotificationToken(userName, currentToken.value))
          ) {
            // Only latch the session guard on success. Latching it up front meant a
            // failed attempt (e.g. the request went out without an auth token) was
            // never retried for the rest of the session.
            alreadyRefreshedThisSession.value = true;
            setTokenSentToServer(true);
          }
        } else if (err) {
          // Permission was revoked or token invalid — prompt user to re-enable
          isEnableNotificationsPopupVisible.value = true;
        }
      }
      return;
    } else if (Notification.permission === "denied") {
      isEnableNotificationsPopupVisible.value = true;
      return;
    } else if (Notification.permission === "default") {
      // User has not been prompted yet — banner will show via showNotificationBanner
      // Don't auto-open the popup; let the banner guide them
    }
  }

  // Option 1: Yes, enable notifications on this device
  async function handleOptIn() {
    console.log("Subscribing notifications...");
    attemptingNotificationsOperation.value = true;
    notificationsError.value = null;

    if (notificationsBlocked.value) {
      notificationsBlocked.value = false;
      await sleep(1000);
    }

    if (isIOS && !isStandalone) {
      isIOSNotInstalledPopupVisible.value = true;
      return;
    }

    notificationsError.value = await tryGetNotificationPermission(true);

    if (hasPermission.value && currentToken.value && !notificationsError.value) {
      var userName = userStore.user?.loginEmail;
      if (userName && (await alertStore.updateNotificationToken(userName, currentToken.value))) {
        setTokenSentToServer(true);
      }
      isEnableNotificationsPopupVisible.value = false;
      attemptingNotificationsOperation.value = false;
      console.log("Notifications enabled.");
    } else if (notificationsError.value) {
      attemptingNotificationsOperation.value = false;
      if (notificationsError.value?.toLowerCase().indexOf("blocked") !== -1) {
        notificationsBlocked.value = true;
        console.log("Notification permission denied with error :", notificationsError.value);
      } else {
        console.log("Error enabling notifications:", notificationsError.value);
      }
    }
  }

  // Option 2: Do not subscribe on this device
  function handleOptOutThisDevice() {
    setManuallyUnsubscribed(true);
    isEnableNotificationsPopupVisible.value = false;
  }

  // Option 3: Opt out on all devices
  async function handleOptOutAllDevices() {
    attemptingNotificationsOperation.value = true;
    const err = await alertStore.setPushOptOut(true);
    if (!err) {
      if (userStore.user) {
        (userStore.user as any).isPushOptedOut = true;
      }
    } else {
      notificationsError.value = err;
    }
    isEnableNotificationsPopupVisible.value = false;
    isConfirmingOptOutAll.value = false;
    attemptingNotificationsOperation.value = false;
  }

  // Unsubscribe from notifications on this device (from the disable popup)
  async function unsubscribeFromNotifications() {
    console.log("Unsubscribing notifications...");
    attemptingNotificationsOperation.value = true;
    await unsubscribeNotifications();
    var userName = userStore.user?.loginEmail;
    if (await alertStore.updateNotificationToken(userName!, null)) {
      setTokenSentToServer(true);
    }
    isDisableNotificationsPopupVisible.value = false;
    attemptingNotificationsOperation.value = false;
    console.log("Notifications unsubscribed.");
  }

  // Auto-start on mount and watch for changes
  onMounted(tryGetNotificationPermissionAtStartup);
  watch(isLoggedIn, tryGetNotificationPermissionAtStartup);
  watch(property, tryGetNotificationPermissionAtStartup);

  return {
    // State
    notificationsEnabled,
    showNotificationBanner,
    isEnableNotificationsPopupVisible,
    isDisableNotificationsPopupVisible,
    isIOSNotInstalledPopupVisible,
    attemptingNotificationsOperation,
    notificationsError,
    notificationsBlocked,
    isConfirmingOptOutAll,
    incomingMessage,
    isTestUser,
    // Browser detection (for popup templates)
    browserName,
    isIOS,
    isAndroid,
    isMacOS,
    isWindows,
    isStandalone,
    isSafari,
    isEdge,
    isChrome,
    isFirefox,
    isiOS17plus,
    // Actions
    dismissNotification,
    handleOptIn,
    handleOptOutThisDevice,
    handleOptOutAllDevices,
    unsubscribeFromNotifications,
    openEnablePopup,
    openDisablePopup,
    askMeNextTime,
  };
}
